import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Field } from "@/components/ui/Field";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { Wordmark } from "@/components/ui/Wordmark";
import {
  AnalyticsIcon,
  ArrowRightIcon,
  EyeIcon,
  EyeOffIcon,
  KeyIcon,
  LockIcon,
  MailIcon,
  PlayersIcon,
  SettingsIcon,
  ShieldCheckIcon,
} from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import { toAbsoluteUrl } from "@/lib/url";
import { LIMITS, emailProblem } from "@/lib/validation";

/**
 * The frame and the fields shared by every screen before a token exists:
 * sign-in and forgot-password.
 *
 * Two columns on a photograph: the arena on the left with the brand, the form
 * on the right in a raised card. The picture is the only place in the whole
 * panel that is allowed to be atmospheric — every screen behind this one is a
 * dense console, and the one moment to look like the game rather than like a
 * table is before anyone has signed in.
 *
 * The form half is drawn on the app's own surface tokens, not on the
 * photograph, so it themes with everything else and keeps its contrast
 * guarantees. The picture half is theme-independent by definition, which is
 * why its type is white and its accent is `arena` rather than `brand`.
 *
 * Below `lg` the left column is gone entirely rather than stacked. Stacking it
 * puts three hundred pixels of marketing above the input box on the screen
 * size most likely to be used one-handed in a hurry.
 */
export function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <main className="relative min-h-full overflow-hidden bg-[oklch(0.16_0.01_265)]">
      <Backdrop />

      {/* Flex rather than grid, specifically because the left column is
          `display: none` below `lg`. A grid keeps the empty track and the card
          ends up laid out against a container wider than the phone it is on;
          a removed flex child takes no space at all. */}
      <div className="relative z-10 mx-auto flex min-h-dvh w-full max-w-[100rem] flex-col items-center justify-center gap-10 px-5 py-10 sm:px-8 lg:flex-row lg:justify-between lg:gap-16 lg:px-14 xl:px-20">
        <ArenaPanel />

        <div className="animate-rise w-full max-w-md shrink-0 lg:max-w-100">
          <div className="bg-surface rounded-panel border-border/60 border p-7 shadow-[0_24px_70px_-20px_oklch(0_0_0/0.55)] sm:p-8">
            <Wordmark className="mb-6" />
            {children}
          </div>
        </div>
      </div>

      {/* Corners. Both are on the photograph rather than in the card: neither
          is part of signing in, and anything inside the card competes with the
          one control that is. */}
      <div className="absolute bottom-4 left-4 z-10 sm:bottom-6 sm:left-6">
        <ThemeToggle />
      </div>

      <p className="absolute right-4 bottom-5 z-10 hidden items-center gap-1.5 text-[0.6875rem] font-medium text-white/55 sm:right-7 sm:flex">
        <ShieldCheckIcon aria-hidden="true" className="size-3.5" />
        Secure &amp; protected
      </p>
    </main>
  );
}

/** Heading and one-line lead for a card. */
export function AuthHeading({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <>
      <h1 className="text-content text-2xl font-bold tracking-tight">{title}</h1>
      {children ? (
        <p className="text-content-muted mt-1 mb-6 text-xs leading-relaxed">{children}</p>
      ) : (
        <div className="mb-6" />
      )}
    </>
  );
}

/**
 * One line, above the button, in a live region that is always mounted. An
 * error placed under the field gets scrolled past on a phone, and a region
 * inserted at the same moment its text appears is frequently missed by a
 * screen reader.
 */
export function FormAlert({ error, notice }: { error: string | null; notice?: string | null }) {
  return (
    <div role="alert" aria-live="assertive" className="empty:hidden">
      {error ? (
        <p className="border-danger/30 bg-danger-soft text-danger rounded-control border px-3 py-2.5 text-xs leading-relaxed">
          {error}
        </p>
      ) : notice ? (
        <p className="border-success/30 bg-success-soft text-success rounded-control border px-3 py-2.5 text-xs leading-relaxed">
          {notice}
        </p>
      ) : null}
    </div>
  );
}

const ICON_CLASS =
  "text-content-subtle pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2";

export function EmailField({
  value,
  onChange,
  autoFocus,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  // Checked on blur, not per key: a half-typed address is not a mistake yet.
  const [left, setLeft] = useState(false);
  const problem = left && value.trim() !== "" ? emailProblem(value) : null;

  return (
    <Field label="Email" required error={problem ?? undefined}>
      {(props) => (
        <div className="relative">
          <MailIcon aria-hidden="true" className={ICON_CLASS} />
          <input
            {...props}
            className="field h-11 py-0 pl-9"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            type="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            autoFocus={autoFocus}
            disabled={disabled}
            placeholder="you@company.com"
            maxLength={LIMITS.EMAIL_MAX}
            onBlur={() => setLeft(true)}
          />
        </div>
      )}
    </Field>
  );
}

/**
 * A password box with a reveal toggle.
 *
 * A reveal, not a plaintext mode: it resets with the component, so a password
 * is never left on screen for the next person at the desk.
 */
export function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
  autoFocus,
  hint,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: "current-password" | "new-password";
  autoFocus?: boolean;
  hint?: string;
  error?: string;
}) {
  const [reveal, setReveal] = useState(false);

  return (
    <Field label={label} required hint={hint} error={error}>
      {(props) => (
        <div className="relative">
          <LockIcon aria-hidden="true" className={ICON_CLASS} />
          <input
            {...props}
            className="field h-11 py-0 pr-11 pl-9"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            type={reveal ? "text" : "password"}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            maxLength={LIMITS.PASSWORD_MAX}
          />
          <button
            type="button"
            onClick={() => setReveal((shown) => !shown)}
            aria-label={reveal ? "Hide password" : "Show password"}
            aria-pressed={reveal}
            className={cn(
              "text-content-subtle hover:text-content absolute top-1/2 right-2 -translate-y-1/2",
              "rounded-control hover:bg-surface-overlay flex size-8 items-center justify-center transition-colors",
            )}
          >
            {reveal ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
          </button>
        </div>
      )}
    </Field>
  );
}

/** Six digits, nothing else — pasted spaces and dashes are dropped. */
export function CodeField({
  label,
  value,
  onChange,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
}) {
  return (
    <Field label={label} required>
      {(props) => (
        <div className="relative">
          <KeyIcon aria-hidden="true" className={ICON_CLASS} />
          <input
            {...props}
            className="field h-11 py-0 pl-9 font-mono tracking-[0.3em]"
            value={value}
            onChange={(event) => onChange(event.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus={autoFocus}
            placeholder="000000"
          />
        </div>
      )}
    </Field>
  );
}

export function SubmitButton({
  busy,
  disabled,
  children,
  busyLabel = "Checking…",
}: {
  busy: boolean;
  disabled: boolean;
  children: React.ReactNode;
  busyLabel?: string;
}) {
  return (
    <Button
      type="submit"
      size="lg"
      className="group h-11 w-full text-[0.9375rem]"
      disabled={busy || disabled}
    >
      {busy ? busyLabel : children}
      {busy ? null : (
        <ArrowRightIcon className="size-4 transition-transform duration-200 group-hover:translate-x-0.5" />
      )}
    </Button>
  );
}

/**
 * "Send a new code", counting down from the API's `resend_available_in`.
 *
 * Disabled rather than hidden while it counts: the person needs to know the
 * option exists and when it arrives, not discover it later.
 */
export function ResendButton({
  secondsLeft,
  busy,
  onClick,
}: {
  secondsLeft: number;
  busy: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || secondsLeft > 0}
      className="text-brand font-medium hover:underline disabled:opacity-50 disabled:hover:no-underline"
    >
      {busy
        ? "Sending…"
        : secondsLeft > 0
          ? `Send a new code in ${String(secondsLeft)}s`
          : "Send a new code"}
    </button>
  );
}

/**
 * What the panel is for, in three words each.
 *
 * Deliberately not a feature list: these are the three sections an operator
 * opens on a normal day, so the strip doubles as a reminder of what they are
 * signing in to do.
 */
const HIGHLIGHTS = [
  { icon: PlayersIcon, label: "Player\nManagement" },
  { icon: AnalyticsIcon, label: "Analytics &\nReports" },
  { icon: SettingsIcon, label: "Game\nSettings" },
];

/**
 * The photograph and the two washes over it.
 *
 * `<img>` rather than a CSS background so the two sizes can be handed to the
 * browser as a `srcset` — the source artwork is 9644px wide and 52MB, which is
 * a fine master and an impossible page weight. `object-center` keeps the two
 * figures in frame as the viewport narrows; the crop loses the empty sand at
 * the sides first, which is what it should lose.
 *
 * Two gradients, not one. The horizontal wash buys contrast for the headline
 * on the left, and the vertical one darkens the sky so the white type at the
 * top of the column does not sit on the brightest part of the picture.
 */
function Backdrop() {
  return (
    <div aria-hidden="true" className="absolute inset-0">
      <img
        src={toAbsoluteUrl("media/images/bg-arena-2560.webp")}
        srcSet={`${toAbsoluteUrl("media/images/bg-arena-1280.webp")} 1280w, ${toAbsoluteUrl(
          "media/images/bg-arena-2560.webp",
        )} 2560w`}
        sizes="100vw"
        alt=""
        // Eager and high priority: it is the entire visual of the first screen
        // of the app, and lazy-loading it would show the flat fallback colour
        // for the first paint of every session.
        loading="eager"
        fetchPriority="high"
        decoding="async"
        className="size-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-linear-to-r from-black/82 via-black/20 via-52% to-black/45" />
      <div className="absolute inset-0 bg-linear-to-b from-black/35 via-transparent via-30% to-black/55" />
    </div>
  );
}

/**
 * The left column: brand, promise, and the three sections behind the door.
 *
 * Hidden below `lg`, and `aria-hidden` with it — every word in here is
 * decorative, and read aloud before the form it is three sentences between a
 * screen-reader user and the one field that matters.
 */
function ArenaPanel() {
  return (
    <div aria-hidden="true" className="hidden max-w-xl lg:block">
      <ArenaLockup />

      <p className="mt-14 text-[0.6875rem] font-semibold tracking-[0.22em] text-white/55 uppercase">
        Welcome back
      </p>

      <h2 className="mt-4 text-4xl leading-[1.12] font-bold tracking-tight text-white xl:text-[2.75rem]">
        Manage. Monitor.
        <br />
        Build a <span className="text-arena">Stronger Arena.</span>
      </h2>

      <p className="mt-5 max-w-md text-sm leading-relaxed text-white/70">
        Track players, tune progression, watch the economy and keep every match running the way it
        should.
      </p>

      <ul className="mt-11 flex gap-9">
        {HIGHLIGHTS.map(({ icon: Icon, label }) => (
          <li key={label} className="flex flex-col gap-2.5">
            <Icon className="text-arena size-5" />
            <span className="text-[0.6875rem] leading-snug font-medium whitespace-pre-line text-white/75">
              {label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The brand over the photograph — set in type, with no mark.
 *
 * The mark is a square crop of this very photograph, so a 36px copy of it on
 * top of the full-bleed original reads as a thumbnail of the page it is on
 * rather than as a logo: muddy at that size, and duplicated art either way.
 * `Wordmark` keeps the mark for surfaces — the sidebar, the card — where it
 * sits on flat paint and has something to be a badge against.
 */
function ArenaLockup() {
  return (
    <div>
      <p className="text-[1.75rem] leading-none font-bold tracking-tight text-white">
        Algorithm Arena
      </p>

      <div className="mt-3 flex items-center gap-3">
        <span aria-hidden="true" className="bg-arena h-0.5 w-7 rounded-full" />
        <p className="text-[0.725rem] font-semibold tracking-[0.3em] text-white/55 uppercase">
          Admin panel
        </p>
      </div>
    </div>
  );
}
